// Greenhold <CreatorEarningsTicker> component. Story 1.26.
//
// First bespoke /src/components/greenhold/* component shipped from the
// `@greenhold/components` workspace package as a result of Story 1.26
// (Story 1.15's <BalanceDisplay> was the prior bespoke component; this
// one ships the FIRST creator-side bespoke surface).
//
// SCREENSHOT-ABLE ARTIFACT PRINCIPLE (UX-DR21 + PRD §Tama journey):
//
// The ticker renders STRUCTURE even when every number is zero. The
// empty-state copy lives BELOW the structure, not in place of it.
// That way the very first screenshot a freshly-approved creator takes
// is structurally identical to a top-earning creator's screenshot —
// the only difference is the numeric values. Without this, the
// recruitment flywheel can never produce a screenshot artifact on
// day one.
//
// DEFERRED STATE BEHAVIORS (intentional v1 scope-out):
//
//   * Rolling-counter animation (UX-DR21 L1629) — only ever renders
//     zero at v1, so animation is invisible regardless. Implement
//     when the first non-zero earnings story ships.
//   * `aria-live="polite"` announce-on-update — wired structurally so
//     screen readers read the value on mount, but no update-cycle
//     announcements yet (the value never changes at v1).
//   * `refreshed` state's pulse animation — same reason.
//
// EUR FORMATTING:
//
// Local `formatEur(cents)` helper renders `€${(cents/100).toFixed(2)}`.
// Intentionally NOT `Intl.NumberFormat` because at v1 the value is
// always €0.00 and the Intl ceremony adds locale-fork risk without
// benefit. Future story can switch to Intl when locale-aware formatting
// matters.

import { cn } from "../lib/utils";

export type CreatorEarningsTickerProps = {
  /**
   * Displayed-name label. Optional at v1: the dashboard page header
   * already carries the page-title context, so the ticker doesn't
   * need to repeat the creator's name. UX-DR21 leaves this open for
   * a future v2 creator-public-profile opt-in surface.
   */
  creator?: string;
  /** Currently informational; ticker always shows cumulative + this-month. */
  period?: "cumulative" | "current-month" | "last-30-days";
  /**
   * `default` — render with whatever values the props carry.
   * `refreshed` — pulse the structure (deferred to a future story).
   * `loading` — render skeleton placeholders in place of the numbers.
   * `empty` — render structure + empty-state copy below.
   */
  state?: "default" | "refreshed" | "loading" | "empty";
  cumulativeEurCents: number;
  currentMonthEurCents: number;
  primaryEurCents: number;
  resaleEurCents: number;
  /** `null` while Epic 3 hasn't shipped `shares`; renders as `—`. */
  holdersCount: number | null;
  /**
   * EUR cents per week for the last 4 weeks. Empty array renders a
   * flat sparkline at zero (the visual goal at v1).
   */
  sparkline4w?: number[];
  className?: string;
};

/**
 * Local euro formatter — avoids Intl ceremony for the v1 zero-state.
 * Examples:
 *   formatEur(0)      => "€0.00"
 *   formatEur(28000)  => "€280.00"
 *   formatEur(1234)   => "€12.34"
 */
function formatEur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

export function CreatorEarningsTicker(props: CreatorEarningsTickerProps) {
  const {
    creator,
    state = "default",
    cumulativeEurCents,
    currentMonthEurCents,
    primaryEurCents,
    resaleEurCents,
    holdersCount,
    sparkline4w = [],
    className,
  } = props;

  const isLoading = state === "loading";
  const isEmpty = state === "empty" || cumulativeEurCents === 0;

  return (
    <div
      className={cn(
        "rounded-2xl border border-brand-forest-200 bg-brand-paper-100 p-6 shadow-sm",
        className,
      )}
    >
      {creator ? (
        <p className="text-xs uppercase tracking-wider opacity-70 font-mono mb-1">
          {creator}
        </p>
      ) : null}

      {/* Cumulative amount — the screenshot-magnet number.
          `aria-live="polite"` so screen readers read it on mount;
          announce-on-update behavior is deferred per module header. */}
      <div aria-live="polite" className="mb-4">
        {isLoading ? (
          <div className="h-12 w-48 rounded-md bg-brand-paper-300 animate-pulse" />
        ) : (
          <p className="font-heading text-5xl font-bold text-brand-forest-900 leading-tight">
            {formatEur(cumulativeEurCents)}
          </p>
        )}
        <p className="text-xs uppercase tracking-wider opacity-60 font-mono mt-1">
          Cumulative earnings
        </p>
      </div>

      <dl className="grid grid-cols-3 gap-4 mb-4">
        <TickerStat
          label="This month"
          value={isLoading ? null : formatEur(currentMonthEurCents)}
        />
        <TickerStat
          label="Primary"
          value={isLoading ? null : formatEur(primaryEurCents)}
        />
        <TickerStat
          label="Resale"
          value={isLoading ? null : formatEur(resaleEurCents)}
        />
      </dl>

      {/* 4-week sparkline placeholder. At v1 the array is empty so
          we render four 1px-tall bars (flat line at zero). Future
          story plugs in real heights derived from weekly EUR cents. */}
      <SparklinePlaceholder weeks={sparkline4w} />

      <p className="text-sm mt-3 opacity-80">
        {holdersCount === null
          ? "—"
          : `${holdersCount} ${holdersCount === 1 ? "fan holds" : "fans hold"} a share of you`}
      </p>

      {isEmpty ? (
        <p className="text-sm mt-3 opacity-70">
          Your earnings will appear here as fans buy + open dossiers featuring you.
        </p>
      ) : null}
    </div>
  );
}

function TickerStat({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider opacity-60 font-mono">
        {label}
      </dt>
      <dd className="font-heading text-xl font-semibold text-brand-forest-800 mt-0.5">
        {value === null ? (
          <span className="inline-block h-6 w-16 rounded-md bg-brand-paper-300 animate-pulse" />
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function SparklinePlaceholder({ weeks }: { weeks: number[] }) {
  // "Rolling 30 days" semantics: when wired, `weeks` will carry 4 EUR-cents
  // values for the last 4 ISO weeks. At v1 the array is empty (or all
  // zeros) so we render a flat-line placeholder.
  const allZero = weeks.length === 0 || weeks.every((w) => w === 0);
  const max = allZero ? 0 : Math.max(...weeks);

  // Always render 4 bars (the "4w" in the prop name is a fixed contract).
  const bars = allZero
    ? [0, 0, 0, 0]
    : (weeks.length === 4 ? weeks : [...weeks, ...Array(4 - weeks.length).fill(0)]).slice(0, 4);

  const ariaLabel = allZero
    ? "Earnings sparkline (last 4 weeks): all zero"
    : `Earnings sparkline (last 4 weeks): ${bars.map((b) => formatEur(b)).join(", ")}`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className="h-10 flex items-end gap-1.5"
    >
      {bars.map((cents, i) => {
        const height = allZero ? 2 : Math.max(2, Math.round((cents / max) * 40));
        return (
          <div
            key={i}
            className="w-3 bg-brand-forest-300 rounded-sm"
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}
